import { useEffect, useMemo, useState } from "react";
import { api, fmtDate, fmtDuration, translateHangupCause } from "@/lib/api";
import {
  Phone,
  PhoneCall,
  PhoneOff,
  PhoneIncoming,
  PhoneOutgoing,
  Radio,
  Server,
  Route,
  Activity,
  Table2,
  GitBranch,
  CheckCircle2,
  XCircle,
  Clock3,
} from "lucide-react";

function elapsed(startedAt) {
  const s = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
  return fmtDuration(s);
}

const END_LABELS = {
  ANSWERED: "Atendida",
  "NO ANSWER": "So chamou",
  BUSY: "Ocupado",
  FAILED: "Negada/Falhou",
  CONGESTION: "Congestionada",
};

const END_TAGS = {
  ANSWERED: "tag-online",
  "NO ANSWER": "tag-warn",
  BUSY: "tag-warn",
  FAILED: "tag-danger",
  CONGESTION: "tag-danger",
};

function FlowNode({ icon: Icon, title, value, tone = "green" }) {
  const color = tone === "amber" ? "var(--accent-amber)" : tone === "blue" ? "var(--accent-blue)" : "var(--accent-green)";
  return (
    <div className="min-w-0 flex-1 border border-[color:var(--border-default)] bg-[color:var(--bg-elevated)] px-3 py-3 rounded-sm">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
        <Icon size={13} style={{ color }} /> {title}
      </div>
      <div className="font-mono text-sm truncate mt-1" title={value || "-"}>{value || "-"}</div>
    </div>
  );
}

function FlowArrow({ label }) {
  return (
    <div className="hidden lg:flex w-20 flex-col items-center justify-center text-[color:var(--text-muted)]">
      <div className="h-px w-full bg-[color:var(--border-default)]" />
      <div className="text-[9px] uppercase tracking-widest mt-1 max-w-full truncate">{label}</div>
    </div>
  );
}

function RecentFinalizations({ rows }) {
  return (
    <div className="sbc-card p-4" data-testid="live-finalizations">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Ultimas finalizacoes</div>
          <div className="text-sm font-medium">Motivo de encerramento das ligacoes recentes</div>
        </div>
        <Clock3 size={16} className="text-[color:var(--text-muted)]" />
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-[color:var(--text-muted)] py-3">Nenhuma finalizacao recente.</div>
      ) : (
        <div className="divide-y divide-[color:var(--border-default)]">
          {rows.map((r) => {
            const answered = r.disposition === "ANSWERED";
            return (
              <div key={r.id || r.call_id} className="py-3 flex flex-wrap items-center gap-3">
                {answered ? <CheckCircle2 size={16} className="text-[color:var(--accent-green)]" /> : <XCircle size={16} className="text-[color:var(--accent-amber)]" />}
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-sm truncate">{r.src || "-"} -&gt; {r.dst || "-"}</div>
                  <div className="text-[10px] text-[color:var(--text-muted)] font-mono truncate">{fmtDate(r.ended_at || r.started_at)} · {translateHangupCause(r.hangup_cause)}</div>
                </div>
                <span className={`sbc-tag ${END_TAGS[r.disposition] || "tag-offline"}`}>{END_LABELS[r.disposition] || r.disposition || "Finalizada"}</span>
                <div className="font-mono text-xs text-[color:var(--text-secondary)]">falado {fmtDuration(r.billsec || 0)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function directionMeta(direction) {
  if (direction === "local") {
    return {
      label: "Interna",
      short: "int",
      icon: Phone,
      color: "text-[color:var(--accent-amber)]",
    };
  }
  if (direction === "inbound") {
    return {
      label: "Entrada",
      short: "in",
      icon: PhoneIncoming,
      color: "text-[color:var(--accent-blue)]",
    };
  }
  return {
    label: "Saida",
    short: "out",
    icon: PhoneOutgoing,
    color: "text-[color:var(--accent-green)]",
  };
}

function CallFlowCard({ row, tick, onHangup }) {
  const meta = directionMeta(row.direction);
  return (
    <div className="sbc-card p-4 space-y-4 fade-in" data-testid={`live-flow-${row.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className={`led ${row.status === "Active" ? "led-green" : row.status === "Ringing" ? "led-amber pulse-ring" : "led-gray"}`} />
            <span className="text-xs uppercase tracking-widest text-[color:var(--text-secondary)]">{row.status}</span>
            <span className="text-[color:var(--border-default)]">|</span>
            <span className="font-mono text-xs text-[color:var(--text-muted)]">{row.call_id}</span>
          </div>
          <div className="font-mono text-2xl mt-1">{row.src || "-"} -&gt; {row.dst || "-"}</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Duracao</div>
            <div className="font-mono text-lg text-[color:var(--accent-green)]" data-elapsed={tick}>{elapsed(row.started_at)}</div>
          </div>
          <button className="sbc-btn sbc-btn-danger" onClick={() => onHangup(row.id)} title="Encerrar">
            <PhoneOff size={12} /> Hangup
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-2 lg:gap-0 items-stretch">
        <FlowNode icon={meta.icon} title={meta.label} value={row.src_ip || row.src} tone="blue" />
        <FlowArrow label="SIP/RTP" />
        <FlowNode icon={Radio} title="SBC" value={row.codec || "media"} tone="green" />
        <FlowArrow label={row.status || "estado"} />
        <FlowNode icon={Route} title="Rota" value={row.operadora || row.presence_id || (row.direction === "local" ? "chamada interna" : "em processamento")} tone="amber" />
        <FlowArrow label="bridge" />
        <FlowNode icon={Server} title="Destino" value={row.ipbx || row.dst} tone="green" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <div><span className="text-[color:var(--text-muted)] uppercase tracking-widest text-[10px]">Direcao</span><div className="font-mono uppercase">{meta.label}</div></div>
        <div><span className="text-[color:var(--text-muted)] uppercase tracking-widest text-[10px]">Codec</span><div className="font-mono">{row.codec || "-"}</div></div>
        <div><span className="text-[color:var(--text-muted)] uppercase tracking-widest text-[10px]">IP origem</span><div className="font-mono truncate">{row.src_ip || "-"}</div></div>
        <div><span className="text-[color:var(--text-muted)] uppercase tracking-widest text-[10px]">Inicio</span><div className="font-mono truncate">{row.started_at || "-"}</div></div>
      </div>
    </div>
  );
}

export default function LiveChannels() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState(null);
  const [tick, setTick] = useState(0);
  const [recentEnded, setRecentEnded] = useState([]);
  const [view, setView] = useState("fluxo");

  useEffect(() => {
    const load = async () => {
      try {
        const [ch, st, cdr] = await Promise.all([
          api.get("/live-channels"),
          api.get("/freeswitch/status"),
          api.get("/cdr", { params: { limit: 8 } }),
        ]);
        setRows(ch.data);
        setStatus(st.data);
        setRecentEnded(cdr.data);
      } catch (_err) {
        // ignore
      }
    };
    load();
    const iv = setInterval(load, 2000);
    const iv2 = setInterval(() => setTick((t) => t + 1), 1000);
    return () => {
      clearInterval(iv);
      clearInterval(iv2);
    };
  }, []);

  const kill = async (id) => {
    try {
      await api.delete(`/live-channels/${id}`);
    } catch (_err) {
      // ignore
    }
  };

  const active = rows.filter((r) => r.status === "Active").length;
  const ringing = rows.filter((r) => r.status === "Ringing").length;
  const byDirection = useMemo(() => ({
    inbound: rows.filter((r) => r.direction === "inbound").length,
    outbound: rows.filter((r) => r.direction === "outbound").length,
    local: rows.filter((r) => r.direction === "local").length,
  }), [rows]);

  return (
    <div className="space-y-5" data-testid="live-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] flex items-center gap-2">
            Real-time channels
            {status && (
              <span className={`sbc-tag ${status.source === "esl" && status.esl_connected ? "tag-online" : "tag-offline"}`} data-testid="live-source-tag">
                {status.source === "esl" ? (status.esl_connected ? "ESL LIVE" : "ESL DOWN") : "SIMULATOR"}
              </span>
            )}
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Chamadas Ativas</h1>
        </div>
        <div className="flex flex-wrap justify-end gap-3">
          <div className="sbc-card px-4 py-2"><div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Ativas</div><div className="text-2xl font-mono font-medium text-[color:var(--accent-green)]" data-testid="live-count-active">{active}</div></div>
          <div className="sbc-card px-4 py-2"><div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Ringing</div><div className="text-2xl font-mono font-medium text-[color:var(--accent-amber)]" data-testid="live-count-ringing">{ringing}</div></div>
          <div className="sbc-card px-4 py-2"><div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Fluxo</div><div className="text-sm font-mono">IN {byDirection.inbound} / OUT {byDirection.outbound} / INT {byDirection.local}</div></div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button className={`sbc-btn ${view === "fluxo" ? "sbc-btn-primary" : ""}`} onClick={() => setView("fluxo")} data-testid="live-view-flow"><GitBranch size={13} /> Fluxo</button>
        <button className={`sbc-btn ${view === "tabela" ? "sbc-btn-primary" : ""}`} onClick={() => setView("tabela")} data-testid="live-view-table"><Table2 size={13} /> Tabela</button>
        <div className="flex items-center gap-2 text-xs font-mono text-[color:var(--text-muted)] ml-auto"><Activity size={13} /> atualizacao 2s</div>
      </div>

      {view === "fluxo" ? (
        <div className="space-y-3">
          {rows.length === 0 ? (
            <div className="sbc-card text-center text-[color:var(--text-muted)] py-8">Nenhuma chamada em curso.</div>
          ) : rows.map((r) => <CallFlowCard key={r.id} row={r} tick={tick} onHangup={kill} />)}
          <RecentFinalizations rows={recentEnded} />
        </div>
      ) : (
        <div className="sbc-card overflow-hidden">
          <table className="sbc-table" data-testid="live-table">
            <thead><tr><th>Status</th><th>Call ID</th><th>Direcao</th><th>Origem</th><th>Destino</th><th>IP</th><th>Codec</th><th>Operadora - IPBX</th><th>Duracao</th><th className="text-right">Acoes</th></tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={10} className="text-center text-[color:var(--text-muted)] py-8">Nenhuma chamada em curso.</td></tr> : rows.map((r) => {
                const meta = directionMeta(r.direction);
                const DirectionIcon = meta.icon;
                return (
                  <tr key={r.id} data-testid={`live-row-${r.id}`} className="fade-in">
                    <td><span className={`led ${r.status === "Active" ? "led-green" : r.status === "Ringing" ? "led-amber pulse-ring" : "led-gray"}`} /><span className="ml-2 text-xs uppercase tracking-widest text-[color:var(--text-secondary)]">{r.status}</span></td>
                    <td className="font-mono text-xs">{r.call_id}</td>
                    <td className="font-mono text-xs uppercase flex items-center gap-1"><DirectionIcon size={12} className={meta.color} />{meta.short}</td>
                    <td className="font-mono">{r.src}</td><td className="font-mono">{r.dst}</td><td className="font-mono text-xs text-[color:var(--text-secondary)]">{r.src_ip}</td><td className="font-mono text-xs">{r.codec}</td>
                    <td className="text-xs text-[color:var(--text-secondary)]">{r.direction === "local" ? "Chamada interna" : `${r.operadora || "-"} - ${r.ipbx || "-"}`}</td>
                    <td className="font-mono"><span data-elapsed={tick} data-testid={`live-elapsed-${r.id}`}>{elapsed(r.started_at)}</span></td>
                    <td className="text-right"><button className="sbc-btn sbc-btn-danger" onClick={() => kill(r.id)} data-testid={`live-hangup-${r.id}`} title="Encerrar"><PhoneOff size={12} /> Hangup</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
